import React, { useMemo, useState } from "react";

import { CampaignCreationModal } from "./CampaignCreationModal";
import { Button, Container, FormEntry, ScreenHeader } from "../../wombat-ui";
import Select from "../../wombat-ui/components/Form/Select";
import useGetViewer from "../../../graphql/hooks/queries/useGetViewer";
import { MadIcon, PlusIcon } from "../../icons";

import { FormStepProps } from "../../../screens/MadFormScreen";
import { useForm } from "react-hook-form";
import useTranslations from "../../../hooks/useTranslations";
import { UserSummaryFragment } from "../../../graphql/fragments";
import { StringParam, useQueryParams } from "use-query-params";

type NextStepButtonControlProps = {
  formState: ReturnType<typeof useForm>["formState"];
  onNext: () => void;
};

const NextStepButtonControl: React.FC<NextStepButtonControlProps> = React.memo(
  ({ formState, onNext }) => {
    const t = useTranslations();

    const disabled = useMemo(() => {
      return (
        formState.errors.campaignId !== undefined ||
        formState.errors.referentId !== undefined ||
        formState.errors.talentsId !== undefined
      );
    }, [formState]);

    return (
      <Button disabled={disabled} onClick={onNext} id="nextStep">
        {t("forms.actions.next")}
      </Button>
    );
  }
);

type MissionType = "available" | "filled";
const FirstStep: React.FC<
  FormStepProps & { companyUsers: UserSummaryFragment[] }
> = ({ onNext, register, unregister, setValue, formState, companyUsers }) => {
  const [innerStep] = useState<MissionType>("filled");
  const [userModalToggled, setUserModalToggled] = useState(false);
  const { data, loading, refetch } = useGetViewer();
  const t = useTranslations();

  const [{ campaignId }] = useQueryParams({
    campaignId: StringParam,
  });

  const campaigns = useMemo(() => data?.viewer?.company?.campaigns || [], [
    data,
  ]);

  return (
    <>
      <ScreenHeader
        icon={MadIcon}
        title={t("mads.new")}
        description={t("mads.whoPartakes")}
        actions={[
          <NextStepButtonControl
            onNext={onNext}
            formState={formState}
            key="next"
          />,
        ]}
      />
      <Container className="flex flex-col flex-grow w-full text-primary-900">
        {innerStep === "filled" && (
          <>
            <div>
              <h3 className="text-xl font-medium text-primary-900">
                {t("mads.chooseParticipants")}
              </h3>
            </div>
            <CampaignCreationModal
              toggled={userModalToggled}
              onClose={() => {
                setUserModalToggled(false);
                refetch();
              }}
            />
          </>
        )}
        <div className="mt-5">
          <div className="grid grid-cols-6 gap-y-4">
            {innerStep === "filled" && (
              <div className="col-span-6 sm:col-span-4">
                <FormEntry title={t("mads.chooseTalents")}>
                  <Select
                    srOnlyLabel
                    id="select-mission-users"
                    searchable
                    loading={loading}
                    label={t("mads.searchTalents") as string}
                    placeholder={t("mads.searchTalents") as string}
                    multiple
                    searchOption={{
                      keys: ["name"],
                      threshold: 0.2,
                      useExtendedSearch: true,
                    }}
                    items={companyUsers}
                    RHF={{
                      register,
                      registerOptions: { validate: (data) => data.length > 0 },
                      unregister,
                      setValue,
                      setValueOptions: {
                        shouldDirty: true,
                        shouldValidate: true,
                      },
                      name: "talentIds",
                    }}
                    renderItem={(item) => item.name}
                    getValue={(item) => item.id}
                  />
                </FormEntry>
              </div>
            )}
            <div className="col-span-6 sm:col-span-4">
              <FormEntry title={t("mads.chooseHS")}>
                <Select
                  srOnlyLabel
                  id="select-mission-campaign"
                  searchable
                  defaultValue={campaigns.find(({id}) => id === campaignId)}
                  label={t("mads.selectHS") as string}
                  placeholder={t("mads.selectHS") as string}
                  searchOption={{
                    keys: ["name"],
                    threshold: 0.2,
                    useExtendedSearch: true,
                  }}
                  loading={loading}
                  RHF={{
                    register,
                    registerOptions: { required: true },
                    unregister,
                    setValue,
                    setValueOptions: {
                      shouldDirty: true,
                      shouldValidate: true,
                    },
                    name: "campaignId",
                  }}
                  items={campaigns}
                  renderItem={(item) => item.name}
                  getValue={(item) => item.id}
                />
                <Button
                  className="-mt-2"
                  variant="link"
                  scale="sm"
                  leadingIcon={PlusIcon}
                  onClick={() => setUserModalToggled(true)}
                  id="addCampaign"
                >
                  {t("mads.createCampaign")}
                </Button>
              </FormEntry>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default React.memo(FirstStep);
